import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { applySiteStyles, type SiteStylesData } from '../utils/siteStyles';

type SiteStyles = SiteStylesData;

interface MenuItem {
  id: string;
  label: string;
  url: string;
  order: number;
  parent_id: string | null;
}

interface FooterLink {
  id: string;
  label: string;
  url: string;
  section: string;
  order: number;
}

interface AnnouncementBarSettings {
  id: string;
  is_enabled: boolean;
  message: string;
  background_color: string;
  text_color: string;
  font_family: string;
}

export interface ShellData {
  siteStyles: SiteStyles | null;
  headerMenu: MenuItem[];
  footerLinks: FooterLink[];
  announcementBar: AnnouncementBarSettings | null;
  logo: string;
}

export interface HomepageContent {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  hero_image: string;
  features: string;
  hero_preset: string;
  logo: string;
  platform_logos: string;
  reviews: string;
  cta_title: string;
  cta_description: string;
  cta_button_text: string;
  cta_button_url: string;
  hero_video_url: string;
  hero_cta_text: string;
  hero_cta_url: string;
  content?: any;
}

interface PageCopy {
  headline: string;
  subheadline: string;
  meta_title: string;
  meta_description: string;
}

export interface HomepageData {
  siteStyles: SiteStyles | null;
  headerMenu: MenuItem[];
  footerLinks: FooterLink[];
  announcementBar: AnnouncementBarSettings | null;
  homepageContent: HomepageContent | null;
  pageCopy: PageCopy | null;
  logo: string;
}

interface CachedShellData {
  data: ShellData;
  timestamp: number;
}

const SHELL_CACHE_KEY = 'shell_data_cache';
const SHELL_CACHE_TTL = 15 * 60 * 1000;
const SITE_STYLES_CACHE_KEY = 'site_styles_cache';

function getCachedShellData(): CachedShellData | null {
  try {
    const cached = localStorage.getItem(SHELL_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch {}
  return null;
}

function setCachedShellData(data: ShellData) {
  try {
    const cacheEntry: CachedShellData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(SHELL_CACHE_KEY, JSON.stringify(cacheEntry));
  } catch {}

  // index.html paints fonts and colors from site_styles_cache before the app loads, so
  // keep it current. Merge so fields other writers store there (e.g. the admin's full row) survive.
  if (data.siteStyles) {
    try {
      const existing = JSON.parse(localStorage.getItem(SITE_STYLES_CACHE_KEY) || '{}');
      localStorage.setItem(SITE_STYLES_CACHE_KEY, JSON.stringify({ ...existing, ...data.siteStyles }));
    } catch {}
  }
}

/**
 * Lightweight hook for the app shell (header, footer, announcement bar, site styles).
 * Runs on every page. Does NOT fetch homepage-specific content.
 */
export function useShellData() {
  const [data, setData] = useState<ShellData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchShellData = useCallback(async (): Promise<ShellData> => {
    const [
      siteStylesResult,
      headerMenuResult,
      footerLinksResult,
      announcementResult,
      logoResult,
    ] = await Promise.all([
      supabase
        .from('site_styles')
        // '*' rather than a column list, so code deployed ahead of a site_styles migration still loads
        .select('*')
        .maybeSingle(),
      supabase
        .from('header_menu')
        .select('*')
        .order('order', { ascending: true }),
      supabase
        .from('footer_menu')
        .select('*')
        .order('order', { ascending: true }),
      supabase
        .from('announcement_bar_settings')
        .select('*')
        .maybeSingle(),
      supabase
        .from('pages')
        .select('logo')
        .eq('slug', 'home')
        .maybeSingle(),
    ]);

    const siteStyles = siteStylesResult.data;
    const headerMenu = headerMenuResult.data || [];
    const footerLinks = footerLinksResult.data || [];
    const announcementBar = announcementResult.data?.is_enabled ? announcementResult.data : null;
    const logo = logoResult.data?.logo || '';

    if (siteStyles) {
      applySiteStyles(siteStyles);
    }

    return {
      siteStyles,
      headerMenu,
      footerLinks,
      announcementBar,
      logo,
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      const cached = getCachedShellData();
      const isCacheValid = cached && (Date.now() - cached.timestamp) < SHELL_CACHE_TTL;

      if (cached?.data) {
        setData(cached.data);
        if (cached.data.siteStyles) {
          applySiteStyles(cached.data.siteStyles);
        }
        setLoading(false);

        if (!isCacheValid) {
          try {
            const freshData = await fetchShellData();
            if (isMounted) {
              setData(freshData);
              setCachedShellData(freshData);
            }
          } catch (error) {
            console.error('Error refreshing shell data:', error);
          }
        }
      } else {
        try {
          const freshData = await fetchShellData();
          if (isMounted) {
            setData(freshData);
            setCachedShellData(freshData);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching shell data:', error);
          if (isMounted) {
            setLoading(false);
          }
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [fetchShellData]);

  return { data, loading };
}

/**
 * Homepage-only data hook. Accepts shell data from the parent to avoid
 * re-fetching site_styles, header_menu, footer_menu, and announcement_bar.
 * Only fetches the 2 homepage-specific queries: pages content + page_copy.
 */
export function useHomepageData(shellData: ShellData | null) {
  const [data, setData] = useState<HomepageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const fetchHomepageContent = useCallback(async (shell: ShellData): Promise<HomepageData> => {
    const [homepageResult, pageCopyResult] = await Promise.all([
      supabase
        .from('pages')
        .select('*')
        .eq('slug', 'home')
        .maybeSingle(),
      supabase
        .from('page_copy')
        .select('headline, subheadline, meta_title, meta_description')
        .eq('page_slug', 'home')
        .maybeSingle(),
    ]);

    const homepageContent = homepageResult.data;
    const pageCopy = pageCopyResult.data;

    return {
      siteStyles: shell.siteStyles,
      headerMenu: shell.headerMenu,
      footerLinks: shell.footerLinks,
      announcementBar: shell.announcementBar,
      homepageContent,
      pageCopy,
      logo: homepageContent?.logo || shell.logo,
    };
  }, []);

  useEffect(() => {
    if (!shellData) return;

    let isMounted = true;

    const initialize = async () => {
      try {
        const freshData = await fetchHomepageContent(shellData);
        if (isMounted) {
          setData(freshData);
          setLoading(false);
          setInitialLoadDone(true);
        }
      } catch (error) {
        console.error('Error fetching homepage data:', error);
        if (isMounted) {
          setLoading(false);
          setInitialLoadDone(true);
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [shellData, fetchHomepageContent]);

  const refresh = useCallback(async () => {
    if (!shellData) return;
    try {
      const freshData = await fetchHomepageContent(shellData);
      setData(freshData);
    } catch (error) {
      console.error('Error refreshing homepage data:', error);
    }
  }, [shellData, fetchHomepageContent]);

  return { data, loading, initialLoadDone, refresh };
}
